import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { DataGridLeafColumn } from '@ibsys/kit';
import type { QualityColumnDescriptor, QualityDetailItem } from '@/entities/dashboards';
import type { ConversationAttributeItem } from '@/entities/attributes';
import { withSecondsSuffix } from '@/entities/attributes';
import { renderQualityAttributeValue } from '@/entities/conversations/model/utils';
import { getExpertReportResultText } from '@/entities/expert-report';
import { useTimeDisplayFormat } from '@/entities/settings';
import { appGeneratePath } from '@/shared/lib';
import { CopyButton } from '@/shared/ui';
import { fmt, scoreClass, totalScoreClass } from './utils';

/** Обязательные main-колонки таблицы (всегда видимы и идут первыми) */
export const MANDATORY_COLUMN_IDS = ['copyLink', 'date', 'consultant', 'totalScore'] as const;

/** Префикс id у динамических колонок «балл блока» (id блока следует после префикса). */
export const BLOCK_COLUMN_PREFIX = 'block_';

/**
 * Результат в ячейке таблицы «Все детали». Само правило показа — общее с колонками списка
 * диалогов ({@link getExpertReportResultText}), здесь задан только вид балла: один знак после
 * запятой. Результата нет — ячейка остаётся пустой (без символа-заглушки), как в гриде диалогов.
 */
const renderResultCell = (
  score: number | null | undefined,
  value: string | null | undefined,
  unit: string | null | undefined,
): string | null => getExpertReportResultText({ score, value, unit }, (points) => fmt(points));

/**
 * Адрес диалога, пригодный для отправки за пределы приложения: относительный путь в
 * переписке или отчёте не откроется.
 */
const buildDialogUrl = (conversationId: number): string =>
  `${window.location.origin}${appGeneratePath('/dialogs/:id', { id: String(conversationId) })}`;

type Params = {
  blockNames: string[];
  availableColumns: QualityColumnDescriptor[];
  availableAttributes: ConversationAttributeItem[];
  blockIdByName: Map<string, number>;
  t: TFunction<'qualityDashboard'>;
};

/**
 * Строит полный набор колонок таблицы «Все детали»: обязательные основные +
 * дополнительные main/attribute/expertReport из availableColumns + по столбцу
 * с баллом на каждый критерий выборки.
 */
export const useAllDetailsColumns = ({
  blockNames,
  availableColumns,
  availableAttributes,
  blockIdByName,
  t,
}: Params): DataGridLeafColumn<QualityDetailItem>[] => {
  const { t: tCommon } = useTranslation('common');
  const timeDisplayFormat = useTimeDisplayFormat();

  return useMemo(() => {
    const mandatory: DataGridLeafColumn<QualityDetailItem>[] = [
      {
        id: 'copyLink',
        header: '',
        accessor: 'conversationId',
        size: 48,
        align: 'center',
        enableResizing: false,
        enableReorder: false,
        cell: ({ row }) => <CopyButton text={buildDialogUrl(row.conversationId)} tooltip={t('allDetails.copyLink')} />,
      },
      {
        id: 'date',
        header: t('allDetails.columns.date'),
        accessor: 'evaluationDate',
        size: 120,
        cell: ({ value }) => (value as string | null) ?? null,
        enableSorting: true,
      },
      {
        id: 'consultant',
        header: t('allDetails.columns.consultant'),
        accessor: 'consultantName',
        size: 180,
        cell: ({ value }) => (value as string) || null,
        enableSorting: true,
        enableFiltering: true,
        filter: { type: 'text' },
      },
      {
        id: 'totalScore',
        header: t('allDetails.columns.totalScore'),
        // Итог показывается таким, каким его дало правило расчёта: балл — баллом, значение — значением
        accessor: (row) => row.totalValue ?? row.totalRating,
        size: 110,
        align: 'center',
        className: 'font-bold',
        // Зона качества есть только у балла: итог-значение долей от максимума не измеряется
        cellClassName: ({ row }) => totalScoreClass(row.totalRating),
        cell: ({ row }) => renderResultCell(row.totalRating, row.totalValue, row.totalUnit),
        enableSorting: true,
        enableFiltering: true,
        filter: { type: 'integer' },
      },
    ];

    // Дополнительные main-колонки (client, externalId и т.п.) из availableColumns,
    // не входящие в обязательные. Если в availableColumns ничего нет — используем
    // дефолтный набор клиента и обращения, чтобы таблица не теряла привычные колонки.
    const fallbackMain: DataGridLeafColumn<QualityDetailItem>[] = [
      {
        id: 'clientName',
        header: t('allDetails.columns.client'),
        accessor: 'clientName',
        size: 180,
        cell: ({ value }) => (value as string) || null,
        enableSorting: true,
        enableFiltering: true,
        filter: { type: 'text' },
      },
      {
        id: 'externalId',
        header: t('allDetails.columns.externalId'),
        accessor: 'externalId',
        size: 120,
        cell: ({ value }) => (value as string) || null,
        enableSorting: true,
        enableFiltering: true,
        filter: { type: 'text' },
      },
    ];

    const mainFromApi = availableColumns
      .filter((c) => c.type === 'main' && !MANDATORY_COLUMN_IDS.includes(c.code as never))
      .filter((c) => c.code !== 'evaluationDate' && c.code !== 'consultantName' && c.code !== 'totalRating')
      .map((c): DataGridLeafColumn<QualityDetailItem> => {
        // clientName и externalId — поддерживают серверную сортировку и фильтр.
        const isSortableMain = c.code === 'clientName' || c.code === 'externalId';
        return {
          id: c.code,
          header: c.title,
          accessor: c.code as keyof QualityDetailItem,
          size: 160,
          cell: ({ value }) => (value as string | number | null) ?? null,
          ...(isSortableMain ? { enableSorting: true, enableFiltering: true, filter: { type: 'text' as const } } : {}),
        };
      });

    const optionalMain = mainFromApi.length > 0 ? mainFromApi : fallbackMain;

    // Атрибутные колонки — значения берутся из items[].attributes[code].
    // Сортировка и фильтрация не поддерживаются (IL: атрибутные колонки не сортируются).
    // Визуальное форматирование ячеек унифицировано с гридом диалогов:
    // CHECKBOX → галочка, INTEGER+SECOND → MM:SS/HH:MM:SS, PERCENT → суффикс %, и т.д.
    // Метаинформация (dataType/displayFormat) берётся из уже загруженной availableAttributes.
    const attributeByPath = new Map(availableAttributes.map((a) => [a.path, a]));
    const attributeCols: DataGridLeafColumn<QualityDetailItem>[] = availableColumns
      .filter((c) => c.type === 'attribute')
      .map((c) => {
        const attribute = attributeByPath.get(c.code);
        return {
          id: `attr_${c.code}`,
          header: withSecondsSuffix(c.title, attribute, timeDisplayFormat, tCommon('secondsSuffix')),
          accessor: (row) => row.attributes?.[c.code] ?? null,
          size: 160,
          cell: ({ value }) => renderQualityAttributeValue(value as string | null, attribute, timeDisplayFormat),
        };
      });

    // Колонки экспертных заключений (если бэк отдаёт). Без сортировки/фильтра.
    const expertReportCols: DataGridLeafColumn<QualityDetailItem>[] = availableColumns
      .filter((c) => c.type === 'expertReport')
      .map((c) => ({
        id: `er_${c.code}`,
        header: c.title,
        accessor: 'expertReportName',
        size: 160,
        cell: ({ value }) => (value as string) || null,
      }));

    // Критериальные колонки: столбец с баллом на каждый критерий выборки.
    const criteriaCols = blockNames.map((n): DataGridLeafColumn<QualityDetailItem> => {
      const blockId = blockIdByName.get(n);
      const id = typeof blockId === 'number' ? `${BLOCK_COLUMN_PREFIX}${blockId}` : `block_name_${n}`;
      return {
        id,
        header: n,
        accessor: (row) => {
          const block = row.blocks.find((b) => b.blockName === n);
          return block?.value ?? block?.rating ?? null;
        },
        size: 110,
        align: 'center',
        className: 'font-bold',
        cellClassName: ({ row }) => scoreClass(row.blocks.find((b) => b.blockName === n)?.rating ?? null),
        cell: ({ row }) => {
          const block = row.blocks.find((b) => b.blockName === n);
          return renderResultCell(block?.rating, block?.value, block?.unit);
        },
        ...(typeof blockId === 'number'
          ? { enableSorting: true, enableFiltering: true, filter: { type: 'integer' as const } }
          : {}),
      };
    });

    return [...mandatory, ...optionalMain, ...attributeCols, ...expertReportCols, ...criteriaCols];
  }, [t, tCommon, timeDisplayFormat, blockNames, availableColumns, availableAttributes, blockIdByName]);
};
